import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/auth';

const api = axios.create({ baseURL: '/api', timeout: 60000 });
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const CURRENCY_SYMBOLS = { USD: '$', HKD: 'HK$', SGD: 'S$', GBP: '£', AUD: 'A$', JPY: '¥' };

const fmt = {
  currency: (n, cur = 'USD') => {
    if (n == null) return '—';
    const sym = CURRENCY_SYMBOLS[cur] || cur + ' ';
    return `${sym}${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  },
  usd: (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—',
  pct: (n) => n != null ? `${n >= 0 ? '+' : ''}${Number(n).toFixed(2)}%` : '—',
  shares: (n) => n != null ? Number(n).toLocaleString('en-US', { maximumFractionDigits: 4 }) : '—',
};
const colorPct = (n) => n == null ? 'var(--text-muted)' : n >= 0 ? 'var(--green)' : 'var(--red)';

const SECTOR_COLORS = [
  '#3b82f6','#8b5cf6','#f97316','#22c55e','#ec4899',
  '#eab308','#ef4444','#06b6d4','#a78bfa','#84cc16','#64748b',
];

// ── Donut chart for sector allocation ──────────────────────────
function DonutChart({ data, total, size = 220 }) {
  if (!data || !data.length) return null;
  const r = size * 0.36, cx = size / 2, cy = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--bg-surface)" strokeWidth="28" />
      {data.map((d, i) => {
        const dash = (d.pct / 100) * circumference;
        const gap = circumference - dash;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={SECTOR_COLORS[i % SECTOR_COLORS.length]} strokeWidth="28"
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-offset * circumference / 100}
            style={{ transition: 'stroke-dasharray 0.5s ease' }}
          />
        );
        offset += d.pct;
        return el;
      })}
      <text x={cx} y={cy - 8} textAnchor="middle" fill="var(--text-muted)" fontSize="11" fontFamily="system-ui">TOTAL</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fill="var(--text-primary)" fontSize="16" fontWeight="700" fontFamily="JetBrains Mono, monospace">
        {total >= 1e6 ? `$${(total/1e6).toFixed(2)}M` : `$${(total/1e3).toFixed(1)}K`}
      </text>
    </svg>
  );
}

// ── Risk Analysis Panel ────────────────────────────────────────
function RiskBadge({ score }) {
  const level = score <= 3 ? 'Low' : score <= 5 ? 'Moderate' : score <= 7 ? 'High' : 'Very High';
  const color = score <= 3 ? 'var(--green)' : score <= 5 ? 'var(--yellow)' : 'var(--red)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ position: 'relative', width: 64, height: 64 }}>
        <svg width="64" height="64" viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="26" fill="none" stroke="var(--bg-surface)" strokeWidth="8" />
          <circle cx="32" cy="32" r="26" fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={`${(score / 10) * 163} 163`} strokeDashoffset="41"
            strokeLinecap="round" />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
          <span style={{ fontSize: 16, fontWeight: 800, color, fontFamily: 'JetBrains Mono, monospace' }}>{score}</span>
          <span style={{ fontSize: 7, color: 'var(--text-muted)', fontWeight: 700 }}>/10</span>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color }}>{level} Risk</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Portfolio Risk Score</div>
      </div>
    </div>
  );
}

function RiskAnalysisPanel({ analysis, onAnalyze, loading }) {
  if (loading) return (
    <div className="card" style={{ marginTop: 16 }}>
      {[100, 60, 80, 40].map((w, i) => (
        <div key={i} className="skeleton" style={{ height: 14, width: `${w}%`, marginBottom: 10, borderRadius: 4 }} />
      ))}
      <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, marginTop: 8 }}>Analysing portfolio risk…</div>
    </div>
  );

  if (!analysis) return (
    <div className="card" style={{ marginTop: 16, textAlign: 'center', padding: '32px 24px' }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>🧠</div>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>AI Portfolio Risk Analysis</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, maxWidth: 380, margin: '0 auto 20px' }}>
        AI risk assessment covering moat quality, valuation discipline, concentration, momentum alignment, and personalised recommendations.
      </div>
      <button className="btn btn-primary" onClick={onAnalyze}>⚡ Analyse Portfolio Risk</button>
    </div>
  );

  const { overallRisk, riskScore, summary, strengths = [], risks = [], recommendations = [],
    concentration, diversification, volatilityAssessment, sectorAllocation = [],
    portfolioAlignment, exitWatchlist = [] } = analysis;
  const vmiAlignment = portfolioAlignment || analysis.vmiAlignment;

  const vmiColor = vmiAlignment?.score >= 7 ? 'var(--green)' : vmiAlignment?.score >= 4 ? '#f59e0b' : 'var(--red)';

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <RiskBadge score={riskScore} />
        <button className="btn btn-ghost" onClick={onAnalyze} style={{ fontSize: 12 }}>↺ Re-analyse</button>
      </div>

      <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.75, marginBottom: 20 }}>{summary}</p>

      {/* Portfolio Alignment Block */}
      {vmiAlignment && (
        <div style={{ background: 'var(--bg-surface)', borderRadius: 10, padding: '14px 16px', marginBottom: 20, border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Portfolio Alignment</div>
            <div style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 15, color: vmiColor }}>
              {vmiAlignment.score}/10
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              <span style={{ fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Moat Quality</span>
              {vmiAlignment.moatAssessment}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              <span style={{ fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Valuation Discipline</span>
              {vmiAlignment.valuationDiscipline}
            </div>
            {vmiAlignment.momentumNote && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', gridColumn: '1/-1' }}>
                <span style={{ fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 2 }}>Momentum</span>
                {vmiAlignment.momentumNote}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--green)', marginBottom: 8 }}>✓ Strengths</div>
          {strengths.map((s, i) => (
            <div key={i} style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, display: 'flex', gap: 6 }}>
              <span style={{ color: 'var(--green)', flexShrink: 0 }}>•</span>{s}
            </div>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--red)', marginBottom: 8 }}>⚠ Risks</div>
          {risks.map((r, i) => (
            <div key={i} style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, display: 'flex', gap: 6 }}>
              <span style={{ color: 'var(--red)', flexShrink: 0 }}>•</span>{r}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        {concentration && (
          <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Concentration</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Top 3: {concentration.top3Weight?.toFixed(1)}%</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{concentration.assessment}</div>
          </div>
        )}
        {diversification && (
          <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Diversification</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Score: {diversification.score}/10</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{diversification.assessment}</div>
          </div>
        )}
      </div>

      {volatilityAssessment && (
        <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: '12px 14px', marginBottom: 20, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Volatility Outlook</span>
          {volatilityAssessment}
        </div>
      )}

      {exitWatchlist.length > 0 && (
        <div style={{ background: '#7f1d1d22', border: '1px solid #f87171', borderRadius: 8, padding: '12px 14px', marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#f87171', marginBottom: 8 }}>🚨 Exit Watchlist</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {exitWatchlist.map((sym, i) => (
              <span key={i} style={{ background: '#f8717122', color: '#f87171', fontWeight: 700, fontSize: 12, padding: '2px 8px', borderRadius: 4, border: '1px solid #f87171' }}>{sym}</span>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#f87171', marginTop: 6, opacity: 0.8 }}>These holdings may warrant a review — check chart & fundamentals before holding</div>
        </div>
      )}

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--accent)', marginBottom: 10 }}>💡 Recommendations</div>
        {recommendations.map((r, i) => (
          <div key={i} style={{
            fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8,
            padding: '8px 12px', background: 'var(--accent-dim)', borderRadius: 6,
            borderLeft: '2px solid var(--accent)', display: 'flex', gap: 8,
          }}>
            <span style={{ fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>{i + 1}.</span>{r}
          </div>
        ))}
      </div>

      <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'center', marginTop: 16, lineHeight: 1.6 }}>
        AI-generated portfolio analysis · Not financial advice
      </div>
    </div>
  );
}

// ── Broker color presets ──────────────────────────────────────
const BROKER_PRESETS = [
  { name: 'Webull',  color: '#00c7ae', icon: '📈' },
  { name: 'eToro',   color: '#2dbe60', icon: '🌿' },
  { name: 'Moomoo',  color: '#ff6a00', icon: '🐄' },
  { name: 'Tiger',   color: '#ff4d4d', icon: '🐯' },
  { name: 'IBKR',    color: '#cc0000', icon: '🏦' },
  { name: 'TD Ameritrade', color: '#007dc1', icon: '🏛️' },
  { name: 'Futu/MooMoo', color: '#f5a623', icon: '📊' },
  { name: 'Custom',  color: '#6366f1', icon: '💼' },
];
const COLOR_SWATCHES = ['#6366f1','#3b82f6','#22c55e','#f59e0b','#ef4444','#ec4899','#06b6d4','#8b5cf6','#00c7ae','#ff6a00','#2dbe60','#ff4d4d'];

// ── Account Modal (create / edit) ─────────────────────────────
function AccountModal({ account, onClose, onSaved }) {
  const [name, setName] = useState(account?.name || '');
  const [broker, setBroker] = useState(account?.broker || '');
  const [color, setColor] = useState(account?.color || '#6366f1');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const applyPreset = (p) => { setName(p.name); setBroker(p.name); setColor(p.color); };

  const save = async () => {
    if (!name.trim()) return setError('Account name is required');
    setSaving(true); setError(null);
    try {
      if (account) {
        await api.put(`/portfolio/accounts/${account.id}`, { name, broker, color }, auth());
      } else {
        await api.post('/portfolio/accounts', { name, broker, color }, auth());
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{account ? 'Edit Account' : 'New Brokerage Account'}</div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}>✕</button>
        </div>

        {!account && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 8 }}>Quick Select</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {BROKER_PRESETS.map(p => (
                <button key={p.name} onClick={() => applyPreset(p)}
                  style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${broker === p.name ? p.color : 'var(--border)'}`,
                    background: broker === p.name ? `${p.color}22` : 'var(--bg-surface)',
                    color: broker === p.name ? p.color : 'var(--text-secondary)',
                    cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.15s' }}>
                  {p.icon} {p.name}
                </button>
              ))}
            </div>
          </>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Account Name *</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. My eToro Account" />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Broker (optional)</label>
            <input className="input" value={broker} onChange={e => setBroker(e.target.value)} placeholder="e.g. eToro, Webull, Moomoo" />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Colour</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {COLOR_SWATCHES.map(c => (
                <button key={c} onClick={() => setColor(c)} style={{
                  width: 26, height: 26, borderRadius: '50%', background: c, border: `3px solid ${color === c ? 'white' : 'transparent'}`,
                  cursor: 'pointer', boxShadow: color === c ? `0 0 0 2px ${c}` : 'none', transition: 'all 0.15s',
                }} />
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{name || 'Account Name'}</div>
          {broker && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {broker}</div>}
        </div>

        {error && <div className="error-msg" style={{ marginTop: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ flex: 1, background: color }}>
            {saving ? 'Saving…' : account ? 'Update' : 'Create Account'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Upload Modal ───────────────────────────────────────────────
function EToroConnectModal({ accountId, onClose, onSynced }) {
  const [apiKey, setApiKey] = useState('');
  const [userKey, setUserKey] = useState('');
  const [connected, setConnected] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/portfolio/etoro/credentials${accountId ? `?accountId=${accountId}` : ''}`, auth())
      .then(r => { setConnected(r.data.connected); setSavedAt(r.data.savedAt); })
      .catch(() => {});
  }, [accountId]);

  const save = async () => {
    if (!apiKey.trim() || !userKey.trim()) return setError('Both API Key and User Key are required');
    setLoading(true); setError(null);
    try {
      await api.post('/portfolio/etoro/credentials', { apiKey: apiKey.trim(), userKey: userKey.trim(), accountId }, auth());
      setConnected(true); setSavedAt(Date.now()); setApiKey(''); setUserKey('');
    } catch (e) { setError(e.response?.data?.error || 'Failed to save credentials'); }
    finally { setLoading(false); }
  };

  const disconnect = async () => {
    await api.delete(`/portfolio/etoro/credentials${accountId ? `?accountId=${accountId}` : ''}`, auth());
    setConnected(false); setSavedAt(null); setResult(null);
  };

  const sync = async () => {
    setSyncing(true); setError(null); setResult(null);
    try {
      const r = await api.post('/portfolio/etoro/sync', { accountId }, auth());
      setResult(r.data);
      if (r.data.synced > 0) onSynced?.();
    } catch (e) {
      const msg = e.response?.data?.error || 'Sync failed';
      const raw = e.response?.data?.raw;
      setError(raw ? `${msg}\n\nRaw response: ${raw}` : msg);
    }
    finally { setSyncing(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 460, margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>🌿 Connect eToro</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Sync positions directly from your eToro real account</div>
          </div>
          <button className="btn-ghost" onClick={onClose}>✕</button>
        </div>

        {!connected ? (
          <div>
            <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>How to get your API keys:</strong><br />
              1. Go to <strong>api-portal.etoro.com</strong><br />
              2. Log in → Create an application → Copy <strong>API Key</strong> and <strong>User Key</strong><br />
              3. Ensure scope includes <strong>portfolio:read</strong>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="label">API Key (x-api-key)</label>
              <input className="input" type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="Paste your eToro API key" />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="label">User Key (x-user-key)</label>
              <input className="input" type="password" value={userKey} onChange={e => setUserKey(e.target.value)} placeholder="Paste your eToro user key" />
            </div>
            {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12, whiteSpace: 'pre-wrap' }}>{error}</div>}
            <button className="btn-primary" onClick={save} disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Saving…' : 'Save & Connect'}
            </button>
          </div>
        ) : (
          <div>
            <div style={{ background: '#22c55e18', border: '1px solid #22c55e44', borderRadius: 8, padding: 12, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>✅</span>
              <div>
                <div style={{ fontWeight: 600, color: '#22c55e', fontSize: 13 }}>eToro Connected</div>
                {savedAt && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Credentials saved {new Date(savedAt).toLocaleDateString()}</div>}
              </div>
            </div>

            {result && (
              <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13 }}>
                <div style={{ fontWeight: 700, color: 'var(--green)', marginBottom: 4 }}>✅ Sync complete</div>
                <div style={{ color: 'var(--text-secondary)' }}>{result.message}</div>
                {result.synced < result.total && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>
                    {result.total - result.synced} positions skipped (missing price or units)
                  </div>
                )}
              </div>
            )}

            {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{error}</div>}

            <button className="btn-primary" onClick={sync} disabled={syncing} style={{ width: '100%', marginBottom: 10 }}>
              {syncing ? '⏳ Syncing from eToro…' : '🔄 Sync Positions Now'}
            </button>
            <button onClick={disconnect} style={{ width: '100%', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 0', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer' }}>
              Disconnect eToro
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function UploadModal({ onClose, onSuccess, accountId }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setLoading(true); setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (accountId) form.append('account_id', accountId);
      const { data } = await api.post('/portfolio/upload', form, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Import Portfolio from Excel</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>AI will read and map your columns automatically</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}>✕</button>
        </div>

        {!result ? (
          <>
            {/* Drop zone */}
            <div
              ref={dropRef}
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              style={{
                border: `2px dashed ${file ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 10, padding: '32px 24px', textAlign: 'center',
                cursor: 'pointer', background: file ? 'var(--accent-dim)' : 'var(--bg-surface)',
                transition: 'all 0.15s', marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
              {file ? (
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 14 }}>{file.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{(file.size / 1024).toFixed(0)} KB · Click to change</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-secondary)' }}>Drop Excel file here or click to browse</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>.xlsx · .xls supported · max 10MB</div>
                </div>
              )}
              <input ref={inputRef} type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} />
            </div>

            {/* Format hint */}
            <div style={{ background: 'var(--bg-surface)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7 }}>
              <strong style={{ color: 'var(--text-secondary)' }}>Accepted columns (any name, AI maps them):</strong><br />
              Ticker / Symbol · Shares / Quantity · Average Cost / Buy Price · Company Name
            </div>

            {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn-primary" onClick={upload} disabled={!file || loading} style={{ flex: 1 }}>
                {loading ? '⏳ Processing…' : '⚡ Import with AI'}
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{result.imported} holdings imported</div>
            {result.skipped > 0 && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>{result.skipped} rows skipped</div>}
            {result.notes && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, background: 'var(--bg-surface)', borderRadius: 8, padding: '10px 14px' }}>{result.notes}</div>}
            <button className="btn btn-primary" onClick={() => { onSuccess(); onClose(); }} style={{ width: '100%' }}>View Portfolio</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add/Edit Holding Modal ─────────────────────────────────────
function HoldingModal({ holding, onClose, onSaved, accountId }) {
  const [symbol, setSymbol] = useState(holding?.symbol || '');
  const [shares, setShares] = useState(holding?.shares || '');
  const [avgCost, setAvgCost] = useState(holding?.avg_cost || '');
  const [note, setNote] = useState(holding?.note || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Normalise symbol: uppercase letters/digits, preserve dots for HK (.HK) etc.
  const normaliseSymbol = (raw) => raw.toUpperCase().replace(/[^A-Z0-9.]/g, '');

  const save = async () => {
    if (!symbol || !shares || !avgCost) return setError('Symbol, shares and avg cost are required');
    setSaving(true); setError(null);
    try {
      if (holding) {
        // Edit: use PUT with the existing id
        await api.put(`/portfolio/${holding.id}`, {
          symbol: normaliseSymbol(symbol),
          shares: parseFloat(shares),
          avg_cost: parseFloat(avgCost),
          note,
          company_name: holding.company_name,
          sector: holding.sector,
          asset_class: holding.asset_class,
        }, auth());
      } else {
        await api.post('/portfolio', { symbol: normaliseSymbol(symbol), shares: parseFloat(shares), avg_cost: parseFloat(avgCost), note, account_id: accountId || null }, auth());
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 380 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{holding ? 'Edit Holding' : 'Add Holding'}</div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Ticker Symbol</label>
            <input className="input" value={symbol} onChange={e => setSymbol(normaliseSymbol(e.target.value))} placeholder="AAPL / 700.HK / BTC-USD" />
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>US stocks: AAPL · HK: 700.HK · SGX: D05.SI · Crypto: BTC-USD, ETH-USD</div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Shares / Units</label>
            <input className="input" type="number" value={shares} onChange={e => setShares(e.target.value)} placeholder="100" min="0" step="any" />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Average Cost (per share)</label>
            <input className="input" type="number" value={avgCost} onChange={e => setAvgCost(e.target.value)} placeholder="150.00" min="0" step="0.01" />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Note (optional)</label>
            <input className="input" value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. long-term hold" />
          </div>
        </div>
        {error && <div className="error-msg" style={{ marginTop: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ flex: 1 }}>{saving ? 'Saving…' : holding ? 'Update' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Add all holdings to watchlist ─────────────────────────────
function AddToWatchlistButton({ holdings, watchlist }) {
  const [done, setDone] = useState(false);
  const handleClick = async () => {
    const symbols = [...new Set(holdings.map(h => h.symbol).filter(Boolean))];
    for (const sym of symbols) await watchlist?.add(sym);
    setDone(true);
    setTimeout(() => setDone(false), 2000);
  };
  return (
    <button className="btn btn-ghost" onClick={handleClick} style={{ fontSize: 13 }}>
      {done ? '✓ Added!' : '★ Add All to Watchlist'}
    </button>
  );
}

// ── Main PortfolioPage ─────────────────────────────────────────
export default function PortfolioPage({ user, onSelectStock, watchlist }) {
  const [accounts, setAccounts] = useState([]);
  const [activeAccountId, setActiveAccountId] = useState(null); // null = "All"
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showEtoro, setShowEtoro] = useState(false);
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [editHolding, setEditHolding] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisCache, setAnalysisCache] = useState({}); // keyed by account id ('all' or account_id)
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [tab, setTab] = useState('holdings');
  const [isPro, setIsPro] = useState(!!user?.is_pro);

  useEffect(() => {
    if (user?.is_pro) { setIsPro(true); return; }
    api.get('/auth/me', auth())
      .then(r => { if (r.data?.user?.is_pro) setIsPro(true); })
      .catch(() => {});
  }, [user]);

  const loadAccounts = async () => {
    try {
      const { data } = await api.get('/portfolio/accounts', auth());
      setAccounts(data);
    } catch {}
  };

  const load = async (accId) => {
    const id = accId !== undefined ? accId : activeAccountId;
    setLoading(true); setError(null);
    try {
      const url = id ? `/portfolio?account_id=${id}` : '/portfolio';
      const { data } = await api.get(url, auth());
      setHoldings(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load portfolio');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    loadAccounts();
    load();
    // Load cached analysis for "All Accounts" from server
    api.get('/portfolio/analyze', auth()).then(r => {
      if (r.data) {
        setAnalysis(r.data);
        setAnalysisCache(prev => ({ ...prev, all: r.data }));
      }
    }).catch(() => {});
  }, []);

  const switchAccount = (id) => {
    setActiveAccountId(id);
    const key = id ?? 'all';
    setAnalysis(analysisCache[key] || null);
    load(id);
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this holding?')) return;
    await api.delete(`/portfolio/${id}`, auth());
    load();
  };

  const removeAccount = async (acc) => {
    if (!window.confirm(`Delete "${acc.name}" and all its holdings?`)) return;
    await api.delete(`/portfolio/accounts/${acc.id}`, auth());
    await loadAccounts();
    if (activeAccountId === acc.id) switchAccount(null);
  };

  const analyze = async () => {
    setAnalyzeLoading(true);
    try {
      const body = activeAccountId ? { account_id: activeAccountId } : {};
      const { data } = await api.post('/portfolio/analyze', body, auth());
      const key = activeAccountId ?? 'all';
      setAnalysis(data);
      setAnalysisCache(prev => ({ ...prev, [key]: data }));
      setTab('risk');
    } catch (err) {
      alert(err.response?.data?.error || 'Analysis failed');
    } finally { setAnalyzeLoading(false); }
  };

  const activeAccount = accounts.find(a => a.id === activeAccountId) || null;

  // Sorting
  const [sortCol, setSortCol] = useState('marketValue');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const sortedHoldings = [...holdings].sort((a, b) => {
    let av, bv;
    if (sortCol === 'gainLoss')    { av = a.gainLossUsd ?? a.gainLoss ?? 0; bv = b.gainLossUsd ?? b.gainLoss ?? 0; }
    else if (sortCol === 'gainLossPct') { av = a.gainLossPct ?? 0; bv = b.gainLossPct ?? 0; }
    else if (sortCol === 'portfolioPct') { av = a.portfolioPct ?? 0; bv = b.portfolioPct ?? 0; }
    else { av = a.marketValueUsd ?? a.marketValue ?? a.costBasis ?? 0; bv = b.marketValueUsd ?? b.marketValue ?? b.costBasis ?? 0; }
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  // Aggregate stats — all in USD for cross-currency consistency
  const totalValue = holdings.reduce((s, h) => s + (h.marketValueUsd ?? h.costBasisUsd ?? h.costBasis), 0);
  const totalCost = holdings.reduce((s, h) => s + (h.costBasisUsd ?? h.costBasis), 0);
  const totalGainLoss = totalValue - totalCost;
  const totalGainLossPct = totalCost ? (totalGainLoss / totalCost) * 100 : 0;
  const hasMultiCurrency = holdings.some(h => h.currency && h.currency !== 'USD');

  // Sector breakdown
  const sectorMap = {};
  holdings.forEach(h => {
    const s = h.sector || 'Other';
    // Always use USD value so percentages are consistent with totalValue
    sectorMap[s] = (sectorMap[s] || 0) + (h.marketValueUsd ?? h.costBasisUsd ?? h.costBasis ?? 0);
  });
  const sectorTotal = Object.values(sectorMap).reduce((a, b) => a + b, 0);
  const sectorData = Object.entries(sectorMap)
    .map(([sector, val]) => ({ sector, pct: sectorTotal ? (val / sectorTotal) * 100 : 0, value: val }))
    .sort((a, b) => b.pct - a.pct);

  const tabStyle = (active) => ({
    padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
    background: active ? 'var(--accent)' : 'var(--bg-surface)',
    color: active ? '#fff' : 'var(--text-muted)',
  });

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)' }}>My Portfolio</div>
            {isPro && (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', padding: '2px 8px',
                background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                color: '#fff', borderRadius: 6,
              }}>PRO</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{holdings.length} holdings · updated live</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {holdings.length > 0 && (
            <AddToWatchlistButton holdings={holdings} watchlist={watchlist} />
          )}
          {isPro && activeAccountId && (
            <button className="btn btn-ghost" onClick={() => setShowUpload(true)} style={{ fontSize: 13 }}>
              📊 Import Excel
            </button>
          )}
          {isPro && activeAccount?.broker?.toLowerCase().includes('etoro') && (
            <button className="btn btn-ghost" onClick={() => setShowEtoro(true)} style={{ fontSize: 13, color: '#2dbe60', borderColor: '#2dbe6044' }}>
              🌿 Sync eToro
            </button>
          )}
          {activeAccountId && (
            <button className="btn btn-primary" onClick={() => setShowAdd(true)} style={{ fontSize: 13 }}>
              + Add Holding
            </button>
          )}
          {!activeAccountId && accounts.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--bg-surface)', borderRadius: 6, border: '1px solid var(--border)' }}>
              ← Select an account to add holdings
            </div>
          )}
        </div>
      </div>

      {/* Account tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {/* All tab */}
        <button
          onClick={() => switchAccount(null)}
          style={{
            padding: '7px 16px', borderRadius: 20, border: `1px solid ${activeAccountId === null ? 'var(--accent)' : 'var(--border)'}`,
            background: activeAccountId === null ? 'var(--accent)' : 'var(--bg-surface)',
            color: activeAccountId === null ? '#fff' : 'var(--text-muted)',
            cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
          }}>
          🗂 All Accounts
        </button>

        {accounts.map(acc => (
          <div key={acc.id} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => switchAccount(acc.id)}
              style={{
                padding: '7px 16px', borderRadius: 20,
                border: `1px solid ${activeAccountId === acc.id ? acc.color : 'var(--border)'}`,
                background: activeAccountId === acc.id ? `${acc.color}22` : 'var(--bg-surface)',
                color: activeAccountId === acc.id ? acc.color : 'var(--text-muted)',
                cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: 6, paddingRight: 30,
              }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: acc.color, flexShrink: 0 }} />
              {acc.name}
            </button>
            {/* Edit/delete mini buttons, visible on hover via group */}
            <div style={{ position: 'absolute', right: 6, display: 'flex', gap: 2 }}>
              <button onClick={() => setEditAccount(acc)}
                title="Edit account"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 11, padding: '1px 3px', lineHeight: 1 }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>✎</button>
              <button onClick={() => removeAccount(acc)}
                title="Delete account"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, padding: '1px 3px', lineHeight: 1 }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}>×</button>
            </div>
          </div>
        ))}

        <button
          onClick={() => setShowNewAccount(true)}
          style={{
            padding: '7px 14px', borderRadius: 20, border: '1px dashed var(--border)',
            background: 'transparent', color: 'var(--text-muted)',
            cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
          + New Account
        </button>
      </div>

      {/* Summary cards */}
      {holdings.length > 0 && (
        <>
          {hasMultiCurrency && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>🌏</span> Multi-currency portfolio — totals converted to USD at live rates. Individual holdings shown in local currency.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Total Value (USD)', value: fmt.usd(totalValue), color: 'var(--accent)' },
              { label: 'Total Cost (USD)', value: fmt.usd(totalCost), color: 'var(--text-secondary)' },
              { label: 'Unrealised P&L', value: fmt.usd(totalGainLoss), color: colorPct(totalGainLoss) },
              { label: 'Return', value: fmt.pct(totalGainLossPct), color: colorPct(totalGainLossPct) },
            ].map(({ label, value, color }) => (
              <div key={label} className="card" style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 18, fontWeight: 700, color }}>{value}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Tabs */}
      {holdings.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button style={tabStyle(tab === 'holdings')} onClick={() => setTab('holdings')}>📋 Holdings</button>
          <button style={tabStyle(tab === 'allocation')} onClick={() => setTab('allocation')}>🥧 Allocation</button>
          <button style={tabStyle(tab === 'risk')} onClick={() => setTab('risk')}>🧠 Risk Analysis</button>
        </div>
      )}

      {/* Content */}
      {loading && (
        <div>
          {[1,2,3,4].map(i => <div key={i} className="skeleton" style={{ height: 52, borderRadius: 8, marginBottom: 8 }} />)}
        </div>
      )}

      {error && <div className="error-msg">{error}</div>}

      {!loading && holdings.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📂</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            {activeAccountId ? `No holdings in ${activeAccount?.name || 'this account'}` : 'No holdings yet'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
            {accounts.length === 0
              ? 'Create a brokerage account first, then add holdings or import from Excel'
              : 'Add holdings manually or import from Excel'}
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {accounts.length === 0 && (
              <button className="btn btn-primary" onClick={() => setShowNewAccount(true)}>+ Create Account</button>
            )}
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Holding</button>
            {isPro && <button className="btn btn-ghost" onClick={() => setShowUpload(true)}>📊 Import Excel</button>}
            {isPro && activeAccount?.broker?.toLowerCase().includes('etoro') && (
              <button className="btn btn-ghost" onClick={() => setShowEtoro(true)} style={{ color: '#2dbe60' }}>🌿 Sync eToro</button>
            )}
          </div>
        </div>
      )}

      {/* Holdings Table */}
      {!loading && holdings.length > 0 && tab === 'holdings' && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}>
                {[
                  { label: 'Symbol', col: null, align: 'left' },
                  { label: 'Shares', col: null },
                  { label: 'Avg Cost', col: null },
                  { label: 'Current', col: null },
                  { label: 'Market Value', col: 'marketValue' },
                  { label: 'Cost Basis', col: null },
                  { label: 'P&L', col: 'gainLoss' },
                  { label: 'P&L %', col: 'gainLossPct' },
                  { label: 'Portfolio %', col: 'portfolioPct' },
                  { label: '', col: null },
                ].map(({ label, col, align }) => (
                  <th key={label} onClick={col ? () => toggleSort(col) : undefined}
                    style={{
                      padding: '10px 14px', textAlign: align || 'right',
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.05em', whiteSpace: 'nowrap',
                      color: sortCol === col ? 'var(--accent)' : 'var(--text-muted)',
                      cursor: col ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}>
                    {label}{col && (sortCol === col ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ' ⇅')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedHoldings.map(h => (
                <tr key={h.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-surface)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '12px 14px' }} onClick={() => onSelectStock?.(h.symbol)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 14, color: h.isCrypto ? '#f59e0b' : 'var(--accent)' }}>{h.symbol}</div>
                      {h.isCrypto && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: '#f59e0b22', color: '#f59e0b', border: '1px solid #f59e0b55' }}>CRYPTO</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.company_name}</div>
                    {h.sector && !h.isCrypto && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>{h.sector}</div>}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)' }}>{fmt.shares(h.shares)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)' }}>
                    {fmt.usd(h.avg_cost)}
                    {h.currency !== 'USD' && h.fxRate && (
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                        {fmt.currency(h.avg_cost / h.fxRate, h.currency)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-primary)', fontWeight: 600 }}>
                    {fmt.currency(h.currentPrice, h.currency)}
                    {h.changePercent != null && (
                      <div style={{ fontSize: 10, color: colorPct(h.changePercent) }}>{fmt.pct(h.changePercent)}</div>
                    )}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {fmt.usd(h.marketValueUsd ?? h.marketValue)}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)' }}>
                    {fmt.usd(h.costBasisUsd ?? h.costBasis)}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: colorPct(h.gainLossUsd ?? h.gainLoss) }}>
                    {fmt.usd(h.gainLossUsd ?? h.gainLoss)}
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: colorPct(h.gainLossPct) }}>{fmt.pct(h.gainLossPct)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                      <div style={{ width: 48, height: 6, borderRadius: 3, background: 'var(--bg-surface)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.min(h.portfolioPct, 100)}%`, background: 'var(--accent)', borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)', minWidth: 36, textAlign: 'right' }}>{h.portfolioPct?.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button onClick={() => setEditHolding(h)}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, padding: '2px 5px', lineHeight: 1 }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                        title="Edit">✎</button>
                      <button onClick={() => remove(h.id)}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14, padding: '2px 4px', lineHeight: 1 }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                        title="Remove">×</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Allocation Tab */}
      {!loading && holdings.length > 0 && tab === 'allocation' && (
        <div className="card" style={{ display: 'flex', gap: 40, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Donut chart */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <DonutChart data={sectorData} total={totalValue} size={220} />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Sector Allocation</div>
          </div>

          {/* Sector legend + bars */}
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 18 }}>By Sector</div>
            {sectorData.map((s, i) => {
              const color = SECTOR_COLORS[i % SECTOR_COLORS.length];
              return (
                <div key={s.sector} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                    <div style={{ width: 12, height: 12, borderRadius: 3, background: color, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{s.sector}</span>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--text-muted)', marginRight: 8 }}>{fmt.usd(s.value)}</span>
                    <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 14, fontWeight: 800, color, minWidth: 48, textAlign: 'right' }}>{s.pct.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--bg-surface)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${s.pct}%`, background: color, borderRadius: 4, transition: 'width 0.5s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Risk Tab */}
      {!loading && tab === 'risk' && (
        <RiskAnalysisPanel analysis={analysis} onAnalyze={analyze} loading={analyzeLoading} />
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onSuccess={load} accountId={activeAccountId} />}
      {showEtoro && <EToroConnectModal accountId={activeAccountId} onClose={() => setShowEtoro(false)} onSynced={() => { load(); setShowEtoro(false); }} />}
      {showAdd && <HoldingModal onClose={() => setShowAdd(false)} onSaved={load} accountId={activeAccountId} />}
      {editHolding && <HoldingModal holding={editHolding} onClose={() => setEditHolding(null)} onSaved={() => { load(); setEditHolding(null); }} accountId={editHolding.account_id} />}
      {showNewAccount && <AccountModal onClose={() => setShowNewAccount(false)} onSaved={async () => { await loadAccounts(); }} />}
      {editAccount && <AccountModal account={editAccount} onClose={() => setEditAccount(null)} onSaved={async () => { await loadAccounts(); setEditAccount(null); }} />}
    </div>
  );
}
